import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, HeartHandshake, Info, Loader2, Lock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PricingCards from "@/components/billing/PricingCards";
import SupportComparison from "@/components/billing/SupportComparison";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/common/Loading";
import RelatedLinks from "@/components/common/RelatedLinks";
import { toast } from "sonner";

export default function Pricing() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [passBusy, setPassBusy] = useState(false);

  useEffect(() => {
    api
      .get("/billing/plans")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  const claimPass = async () => {
    if (!user) {
      navigate("/register?next=/pricing");
      return;
    }
    setPassBusy(true);
    try {
      await api.post("/billing/free-pass");
      await refresh();
      toast.success("Your 7-Day Recovery Access Pass is active. No card needed.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPassBusy(false);
    }
  };

  if (!data) return <Spinner label="Loading plans" />;

  return (
    <div className="max-w-6xl mx-auto px-4 py-10">
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="font-display text-4xl font-bold text-[#1F2A2E]">
          Affordable memberships, honest pricing
        </h1>
        <p data-testid="pricing-definition" className="mt-3 text-[#1F2A2E] leading-7 font-medium">
          Sponsor Me pricing covers access to platform messaging and connection features, not the
          purchase of sponsorship itself.
        </p>
        <p className="mt-3 text-[#4B5A60] leading-7">
          Browsing sponsors is free forever, and sponsor accounts never pay. Members looking for a
          sponsor get {data.free_message_allowance} free sent messages. After that, a low-cost plan
          unlocks unlimited messaging.
        </p>
      </div>

      {user && !user.has_unlimited && (
        <div
          data-testid="pricing-free-balance"
          className="mt-8 mx-auto max-w-md rounded-2xl bg-white border border-[#E3DED4] p-4 text-center"
        >
          <p className="text-sm text-[#4B5A60]">Your free messages</p>
          <p className="font-display text-2xl font-bold text-[#1F2A2E]">
            {user.free_messages_remaining} of {data.free_message_allowance} remaining
          </p>
        </div>
      )}

      {user?.account_type === "sponsor" && (
        <div className="mt-8 mx-auto max-w-2xl rounded-2xl bg-[#E4F3EA] border border-[#B7DFC7] p-5 text-center">
          <HeartHandshake className="w-6 h-6 mx-auto text-[#1F7A4D]" />
          <p className="mt-2 font-display text-lg font-bold text-[#14603C]">
            You are a sponsor. Messaging is free for you, forever.
          </p>
          <p className="mt-1 text-sm text-[#1F7A4D]">
            Sponsors never pay to use Sponsor Me. No membership required.
          </p>
        </div>
      )}

      <div className="mt-10">
        <PricingCards plans={data.plans} />
      </div>

      <p
        data-testid="renewal-notice"
        className="mt-6 flex items-start gap-2 justify-center text-sm text-[#4B5A60] max-w-2xl mx-auto text-center"
      >
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        {data.renewal_notice}
      </p>

      {/* Transactional trust. Every line here is literally true of the current
          implementation: checkout is Stripe's own hosted session
          (stripe.checkout.Session.create, mode="subscription"), so card details
          are entered on Stripe and never reach this app or its database; the
          access pass genuinely takes no card; and cancellation is self-serve
          through the Stripe billing portal. Deliberately NO payment-brand
          logos, NO security badges, NO money-back guarantee - none of those
          exist or are cleared for use. */}
      <div
        data-testid="payment-trust"
        className="mt-6 mx-auto max-w-3xl rounded-2xl bg-white border border-[#E3DED4] p-5"
      >
        <p className="flex items-center gap-2 font-display text-base font-bold text-[#1F2A2E]">
          <Lock className="w-4 h-4 text-[#1F6F78]" aria-hidden="true" />
          Secure payments powered by Stripe
        </p>
        <ul className="mt-3 grid gap-2 sm:grid-cols-2 text-sm text-[#4B5A60]">
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 text-[#1F7A4D] shrink-0" aria-hidden="true" />
            Card details are entered on Stripe's own checkout page - Sponsor Me never sees or
            stores your full card number
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 text-[#1F7A4D] shrink-0" aria-hidden="true" />
            Cancel any time from your dashboard, and keep access until the date you have paid for
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 text-[#1F7A4D] shrink-0" aria-hidden="true" />
            No hidden charges, and no setup or cancellation fees
          </li>
          <li className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 text-[#1F7A4D] shrink-0" aria-hidden="true" />
            No card required for the {data.free_pass_days}-Day Recovery Access Pass
          </li>
        </ul>
      </div>

      <SupportComparison plans={data.plans} freePassDays={data.free_pass_days} />

      <div className="mt-12 grid gap-6 lg:grid-cols-2">
        <div className="sm-card p-6 bg-white">
          <h2 className="font-display text-xl font-bold text-[#1F2A2E]">What a membership includes</h2>
          <ul className="mt-4 space-y-2.5">
            {data.benefits.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-[#1F2A2E]">
                <Check className="w-4 h-4 mt-0.5 text-[#1F7A4D] shrink-0" /> {b}
              </li>
            ))}
          </ul>
          <div className="mt-5 pt-4 border-t border-[#E3DED4]">
            <p className="text-sm font-bold text-[#1F2A2E]">Always free, for everyone</p>
            <ul className="mt-2 space-y-1.5 text-sm text-[#4B5A60]">
              <li>Browsing and searching sponsor profiles</li>
              <li>Blocking and reporting members</li>
              <li>Emergency and crisis resources</li>
              <li>Basic account access and account deletion</li>
              <li>Sponsor accounts, including unlimited messaging</li>
            </ul>
          </div>
        </div>

        <div
          id="access-pass"
          className="rounded-2xl bg-[#F6E2D6] border border-[#E8C4AC] p-6 flex flex-col"
        >
          <h2 className="font-display text-xl font-bold text-[#7A3E17]">
            Need Access But Can&apos;t Afford It?
          </h2>
          <p className="mt-2 text-sm text-[#7A3E17] leading-6">
            Recovery should never stop because of {"$"}4.99. Request a{" "}
            <strong>{data.free_pass_days}-Day Recovery Access Pass</strong> for unlimited messaging.
            No credit card required, no questions asked.
          </p>
          <div className="mt-4 flex-1">
            {user?.free_pass_used ? (
              <p
                data-testid="free-pass-used-note"
                className="text-sm font-semibold text-[#7A3E17] bg-[#FBEFE6] rounded-xl px-4 py-3"
              >
                You have already used your Recovery Access Pass
                {user.free_pass_expires_at
                  ? ` (expires ${new Date(user.free_pass_expires_at).toLocaleDateString()})`
                  : ""}
                . If you still cannot afford a membership, please reach out to us.
              </p>
            ) : null}
          </div>
          <Button
            onClick={claimPass}
            disabled={passBusy || user?.free_pass_used || user?.account_type === "sponsor"}
            data-testid="claim-free-pass-button"
            className="mt-4 bg-[#D07A4A] hover:bg-[#B96536] text-white"
          >
            {passBusy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              `Request my ${data.free_pass_days}-day access pass`
            )}
          </Button>
        </div>
      </div>
      <RelatedLinks
        heading="Keep reading"
        links={[
          { to: "/sponsors", label: "Browse peer sponsors" },
          { to: "/become-a-sponsor", label: "Become a sponsor, free forever" },
          { to: "/contact", label: "Contact us about billing" },
        ]}
      />
    </div>
  );
}
